import {
  type ErrorResponse,
  isRouteErrorResponse,
  Link,
  useParams,
  useRouteError,
} from "react-router";
import { Icon } from "./ui/icon";
import React from "react";

export const UnauthorizedBoundary = ({ message }: { message: string }) => {
  return (
    <div className="flex flex-col items-center justify-center w-full gap-5 h-content-height">
      <Icon name="lock-open-1" className="-mt-14 h-11 w-11 text-muted" />
      <div className="flex flex-col gap-1 text-center text-card-foreground">
        <h2 className="font-medium text-h2">No tienes acceso a esta ruta</h2>
        <p className="font-normal text-base text-muted-foreground">{message}</p>
      </div>
    </div>
  );
};

type StatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
}) => JSX.Element | null;

export const GeneralErrorBoundary = ({
  defaultStatusHandler = ({ error }) => (
    // <p>
    //   {error.status} {error.data}
    // </p>
    <FallbackErrorScreen />
  ),
  statusHandlers,
  // unexpectedErrorHandler = (error) => <p>{getErrorMessage(error)}</p>,
  unexpectedErrorHandler = () => <FallbackErrorScreen />,
}: {
  defaultStatusHandler?: StatusHandler;
  statusHandlers?: Record<number, StatusHandler>;
  unexpectedErrorHandler?: (error: unknown) => JSX.Element | null;
}) => {
  const error = useRouteError();
  const params = useParams();

  if (typeof document !== "undefined") {
    console.error(error);
  }

  return (
    <div className="flex items-center justify-center h-content-height text-h2 bg-background text-foreground">
      {isRouteErrorResponse(error)
        ? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
            error,
            params,
          })
        : unexpectedErrorHandler(error)}
    </div>
  );
};

export const FallbackErrorScreen = () => {
  const [showMessage, setShowMessage] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowMessage(true);
    }, 2500);

    // Clean up timer when component unmounts
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen font-normal">
      <img
        src="/assets/images/error-boundary.gif"
        className={`md:max-w-xl -mt-10 w-full h-full object-contain fade-mask transition-all duration-1000 ${
          showMessage ? "blur-md" : "blur-[0.5px]"
        }`}
        alt="Error animation"
      />

      {showMessage && (
        <div
          className="min-w-[70%] flex flex-col gap-3 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center bg-black/30 p-6 md:p-12 rounded-lg shadow-lg opacity-0 animate-fade-in"
          style={{
            animation: "fadeIn 1s ease-in-out forwards",
          }}
        >
          <p className="text-base font-semibold">
            Esta página está fuera de juego.
          </p>
          <div className="text-sm text-muted-foreground">
            Ha ocurrido un error inesperado. Intenta actualizar la página para
            continuar,{" "}
            <Link
              to="/"
              className="underline text-foreground"
              viewTransition
              prefetch="intent"
            >
              o regresa al inicio.
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
